import React from 'react';
import { formatTime } from '../../services/rideService';

interface BookingState {
  rideRequestId: string;
  accessToken: string;
  slot: { date: string; startTime: string; endTime: string };
  eventName: string;
  pickupAddress: string;
}

interface BookingConfirmationSectionProps {
  bookingState: BookingState | null;
  onBrowseEvents: () => void;
}

const BookingConfirmationSection: React.FC<BookingConfirmationSectionProps> = ({ bookingState, onBrowseEvents }) => {
  if (!bookingState) {
    return (
      <div className="text-center py-16">
        <p className="text-WHITE/50 mb-4">No booking information found.</p>
        <button onClick={onBrowseEvents} className="text-ORANGE font-bold hover:underline">Browse events</button>
      </div>
    );
  }

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  };

  return (
    <div className="py-4 text-center">
      {/* Success Icon */}
      <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
        <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      </div>

      <h1 className="text-3xl font-bold text-WHITE mb-3" style={{ fontFamily: '"Anton", sans-serif' }}>
        You're In!
      </h1>

      <p className="text-WHITE/60 text-lg mb-6">
        Your free ride to <strong className="text-WHITE">{bookingState.eventName}</strong> is booked.
      </p>

      {/* Ride Details Card */}
      <div className="bg-BLACK/40 backdrop-blur-sm rounded-xl p-5 border border-WHITE/10 mb-8 text-left max-w-md mx-auto">
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-WHITE/50 text-sm">Date</span>
            <span className="font-bold text-WHITE text-sm">{formatDate(bookingState.slot.date)}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-WHITE/50 text-sm">Time Slot</span>
            <span className="font-bold text-WHITE text-sm">{formatTime(bookingState.slot.startTime)} - {formatTime(bookingState.slot.endTime)}</span>
          </div>
          <div className="flex justify-between items-start">
            <span className="text-WHITE/50 text-sm">Pickup</span>
            <span className="font-bold text-WHITE text-sm text-right max-w-[60%]">{bookingState.pickupAddress}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-WHITE/50 text-sm">Status</span>
            <span className="bg-yellow-500/20 text-yellow-300 text-xs font-bold px-3 py-1 rounded-full">
              Pending Confirmation
            </span>
          </div>
        </div>
      </div>

      <p className="text-WHITE/50 text-sm mb-1">Check your email for updates on your ride.</p>
      <p className="text-WHITE/30 text-xs mb-8">We'll confirm your slot and send pickup details shortly.</p>

      {/* Download App CTA */}
      <div className="bg-BLACK/50 backdrop-blur-sm rounded-xl p-6 border border-WHITE/10 max-w-md mx-auto">
        <h2 className="text-xl font-bold text-WHITE mb-2" style={{ fontFamily: '"Anton", sans-serif' }}>
          Get Live Updates in the App
        </h2>
        <p className="text-WHITE/50 text-sm mb-5">
          Download CollectedAF to get real-time ride notifications, see who's at the event, and start collecting.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <a
            href="https://apps.apple.com/us/app/collectedaf/id1592301226"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-WHITE text-BLACK font-bold py-2.5 px-6 rounded-lg hover:opacity-90 transition-opacity text-sm"
          >
            Download for iOS
          </a>
          <a
            href="https://play.google.com/store/apps/details?hl=en_US&id=com.collectedAF.collectedAF"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-WHITE/20 text-WHITE font-bold py-2.5 px-6 rounded-lg hover:opacity-90 transition-opacity text-sm"
          >
            Download for Android
          </a>
        </div>
      </div>

      <div className="mt-8">
        <button onClick={onBrowseEvents} className="text-WHITE/40 hover:text-WHITE text-sm">
          ← Browse more events
        </button>
      </div>
    </div>
  );
};

export default BookingConfirmationSection;
